import { cookieStorage, makePersisted } from "@solid-primitives/storage";
import { PaperclipIcon } from "@storytell/bsky-icons";
import { subscribeToGlobalEvent } from "@storytell/events";
import { PromptEditor } from "@storytell/rich-text";
import type { AssetId, ConceptId, LabelId, ViewId } from "@storytell/types";
import { PmNodeType, type PmVariable } from "@storytell/types/richText";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import clsx from "clsx";
import { HiSolidRocketLaunch } from "solid-icons/hi";
import { type Component, Show, createSignal, onCleanup, onMount } from "solid-js";
import { twMerge } from "tailwind-merge";
import { gateCreditsDescriptions, useGateActionByCredits } from "~/hooks/useGateActionByCredits";
import { useWire } from "~/wire";
import { useAppUI } from "../layouts/minimal/AppUIContext";
import type { PromptBarProps } from "./PromptBar.types";
import { PromptBarProvider, usePromptBarContext } from "./internal/PromptBarContext";
import { CreditsDisplay, FullScreenDropzone, GradientBorder, ScopeDisplay, SubmitButton } from "./internal/components";
import { CompactPreferences } from "./internal/components/CompactPreferences";
import { PromptAutoImprove } from "./internal/components/PromptAutoImprove";
import { PromptBarButton } from "./internal/components/PromptBarButton";
import { PromptBarQuote } from "./internal/components/PromptBarQuote";
import { useDragAndDrop } from "./internal/hooks";

/**
 * Internal component that renders the actual prompt bar UI
 */
const PromptBarContent: Component<{
  quote: () => string;
  setQuote: (value: string) => void;
}> = (props) => {
  const context = usePromptBarContext();
  const wire = useWire();

  // Persist dismiss state in cookies
  const [isDismissed, setIsDismissed] = makePersisted(createSignal(false), {
    storage: cookieStorage,
    name: "at-mention-tip-dismissed",
  });

  const canUploadAssets = () => wire.grants.canCreate("cp.assets");

  onMount(() => {
    const unsubscribeFilePicker = subscribeToGlobalEvent("open-file-picker", () => {
      handlePaperclipClick();
    });

    const unsubscribePromptSubmittedSuccessfully = subscribeToGlobalEvent("prompt-submitted-successfuly", () => {
      context.prompt.editor()?.commands.clearContent();
    });

    const unsubscribeStartPromptWithMention = subscribeToGlobalEvent("start-prompt-with-mention", (ev) => {
      const concept = wire.concepts.get(ev.detail.id as ConceptId);
      if (concept) {
        const node: PmVariable = {
          type: PmNodeType.VariableV1,
          attrs: {
            type: "concept",
            id: ev.detail.id,
            text: concept?.name,
          },
        };
        context.prompt.editor()?.commands.setContent([node]);
        context.prompt.editor()?.commands.focus();
        return;
      }
      const asset = wire.assets.get(ev.detail.id as AssetId);
      if (asset) {
        const node: PmVariable = {
          type: PmNodeType.VariableV1,
          attrs: {
            type: "asset",
            id: ev.detail.id,
            text: asset.filename,
          },
        };
        context.prompt.editor()?.commands.setContent([node]);
        context.prompt.editor()?.commands.focus();
        return;
      }
      const label = wire.labels.get(ev.detail.id as LabelId);
      if (label) {
        const node: PmVariable = {
          type: PmNodeType.VariableV1,
          attrs: {
            type: "label",
            id: ev.detail.id,
            text: label.name,
          },
        };
        context.prompt.editor()?.commands.setContent([node]);
        context.prompt.editor()?.commands.focus();
        return;
      }
      const view = wire.views.get(ev.detail.id as ViewId);
      if (view) {
        const node: PmVariable = {
          type: PmNodeType.VariableV1,
          attrs: {
            type: "view",
            id: ev.detail.id,
            text: view.name,
          },
        };
        context.prompt.editor()?.commands.setContent([node]);
        context.prompt.editor()?.commands.focus();
        return;
      }
    });

    const unsubscribeUploadFiles = subscribeToGlobalEvent("upload-files", (ev) => {
      uploadFiles(ev.detail.files);
    });

    const unsubscribeFocusPrompt = subscribeToGlobalEvent("focus-prompt-bar", () => {
      context.prompt.editor()?.commands.focus();
    });

    onCleanup(() => {
      unsubscribeFilePicker();
      unsubscribePromptSubmittedSuccessfully();
      unsubscribeStartPromptWithMention();
      unsubscribeUploadFiles();
      unsubscribeFocusPrompt();
    });
  });

  const { isDropzoneMode, fileUpload } = useDragAndDrop(canUploadAssets);

  const maxCharacters = 3000;
  const showCountThreshold = 500;
  const shouldShowCount = () => context.input.characterCount() >= showCountThreshold;

  const uploadFiles = async (files: File[]) => {
    try {
      // Create temporary asset entries for immediate UI feedback
      const tempAssets = files.map((file) => {
        const tempId = wire.chat.scope.addUploadingAsset(file.name);
        return { tempId, filename: file.name };
      });

      // Upload files
      await fileUpload.uploadFiles(files, wire.auth.realm().projectId);

      // Clean up any remaining temporary assets
      tempAssets.forEach(({ tempId, filename }) => {
        const stillExists = wire.chat.scope.uploadingAssets().find((asset) => asset.id === tempId);
        if (stillExists) {
          wire.chat.scope.removeUploadingAsset(stillExists.id);
        }
      });
    } catch (error) {
      console.error("Failed to upload files:", error);

      // Update any remaining temporary assets to error state
      const tempAssets = files.map((file) => {
        const tempId = wire.chat.scope.addUploadingAsset(file.name);
        return { tempId };
      });

      tempAssets.forEach(({ tempId }) => {
        const stillExists = wire.chat.scope.uploadingAssets().find((asset) => asset.id === tempId);
        if (stillExists) {
          wire.chat.scope.updateUploadingAssetStatus(stillExists.id, "error");
        }
      });
    }
  };

  const handlePaperclipClick = useGateActionByCredits(
    () => gateCreditsDescriptions.fileUpload,
    () => {
      // Create a file input element
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.accept = "*/*"; // Accept all file types
      input.style.display = "none";

      // Add to DOM temporarily
      document.body.appendChild(input);

      // Handle file selection
      input.addEventListener("change", async (event) => {
        const files = Array.from((event.target as HTMLInputElement).files || []);

        if (files.length > 0) {
          await uploadFiles(files);
        }

        // Clean up the input element
        document.body.removeChild(input);
      });

      // Open file picker
      input.click();
    },
  );

  return (
    <>
      {/* Full-screen dropzone overlay */}
      <Show when={isDropzoneMode()}>
        <FullScreenDropzone />
      </Show>

      <div class="w-full max-w-4xl mx-auto relative" id="prompt-bar">
        <GradientBorder />
        {/* Main Prompt Bar */}
        <div>
          <div class="relative transition-all duration-300 rounded-3xl bg-panel shadow-lg shadow-black/10 dark:shadow-black/30">
            {/* Normal Input State */}
            <div class={twMerge("pt-3 pb-2 space-y-2 transition-opacity duration-300", props.quote() && "pt-0")}>
              <Show when={props.quote()}>
                <PromptBarQuote quote={props.quote()} onClose={() => props.setQuote("")} />
              </Show>

              {/* Scope Display */}
              <ScopeDisplay />

              {/* Text Input */}
              <div class="px-4 mb-3">
                <PromptEditor context={context.prompt} />
              </div>

              {/* Action Bar - Icons and Submit */}
              <div data-testid="prompt-action-bar" class="flex items-center justify-between px-4">
                {/* Left Side Icons */}
                <div class="flex items-center gap-2">
                  <StTooltip
                    content={
                      <p>
                        {canUploadAssets()
                          ? "Upload files or connect data sources."
                          : "Request collaborator permissions from this Project's owner if you want to upload assets."}
                      </p>
                    }
                    placement="top"
                    ignoreSafeArea
                    dontKeepOpenOnHover
                    contentProps={{
                      class: "z-50 bg-panel border border-primary-border rounded-xl shadow-xl",
                    }}
                  >
                    <PromptBarButton
                      as={StTooltipTrigger}
                      aria-label="Upload files"
                      icon={PaperclipIcon}
                      onClick={handlePaperclipClick}
                      type="button"
                      disabled={!canUploadAssets()}
                    />
                  </StTooltip>
                  <PromptAutoImprove />

                  {/* Character Count Indicator */}
                  <Show when={shouldShowCount()}>
                    <div class="text-xs text-muted-text px-2">
                      {context.input.characterCount()}/{maxCharacters}
                    </div>
                  </Show>
                </div>

                {/* Right Side */}
                <div class="flex items-center gap-2 min-w-0">
                  <CompactPreferences />
                  <CreditsDisplay />
                  <SubmitButton onSubmit={context.submission.handleSubmit} />
                </div>
              </div>

              {/* Pro Tip on its own line (Always) */}
              <Show when={context.input.hasInputText() && !isDismissed()}>
                <div class="flex justify-center px-4 pb-2">
                  <div class="relative group cursor-pointer">
                    <div class="text-blue-600 dark:text-blue-400 text-sm flex items-center gap-1 group-hover:text-gray-300 dark:group-hover:text-gray-600 transition-colors duration-200">
                      <HiSolidRocketLaunch class="w-4 h-4 text-blue-600 dark:text-blue-400 group-hover:text-gray-300 dark:group-hover:text-gray-600 transition-colors duration-200" />
                      {/* Mobile version (under 800px) */}
                      <span class="[@media(min-width:800px)]:hidden">
                        Use{" "}
                        <span class="inline-block border border-gray-200 dark:border-gray-700 rounded-md px-1.5 py-0.5 text-xs font-mono bg-gray-50 dark:bg-gray-800/50 group-hover:border-gray-300 dark:group-hover:border-gray-600 group-hover:bg-gray-100 dark:group-hover:bg-gray-700/50 transition-colors duration-200">
                          @
                        </span>{" "}
                        to chat with a view, label, concept or asset{" "}
                      </span>
                      {/* Desktop version (800px and above) */}
                      <span class="hidden [@media(min-width:800px)]:inline">
                        Try typing{" "}
                        <span class="inline-block border border-gray-200 dark:border-gray-700 rounded-md px-1.5 py-0.5 text-xs font-mono bg-gray-50 dark:bg-gray-800/50 group-hover:border-gray-300 dark:group-hover:border-gray-600 group-hover:bg-gray-100 dark:group-hover:bg-gray-700/50 transition-colors duration-200">
                          @
                        </span>{" "}
                        to chat with a specific view, label, concept or asset{" "}
                      </span>
                    </div>

                    {/* Hover Overlay */}
                    <div class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      <div class="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 px-3 py-1 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <span class="flex items-center gap-1">
                          <HiSolidRocketLaunch class="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <span class="font-medium">Pro-tip:</span>
                        </span>
                        <a
                          href="http://go.storytell.ai/learn-at-mentions"
                          target="_blank"
                          rel="noreferrer"
                          class="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline pointer-events-auto"
                        >
                          Learn more
                        </a>
                        <span class="mx-2 text-gray-400">|</span>
                        <button
                          type="button"
                          onClick={() => setIsDismissed(true)}
                          class="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline pointer-events-auto"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Show>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Main PromptBar component - the public API
 *
 * A sophisticated prompt input interface with:
 * - Dynamic mode switching (chat/agent/workflow)
 * - File drag and drop support
 * - Credit display and management
 * - Animated gradient border
 * - Responsive submit button (mic/arrow/stop)
 * - Character counting and limits
 * - Streaming state awareness for UI transformations
 */
export const PromptBar: Component<PromptBarProps> = (props) => {
  const [quote, setQuote] = createSignal("");

  onMount(() => {
    const unsubscribeQuote = subscribeToGlobalEvent("quote-in-prompt-bar", (ev) => {
      setQuote(ev.detail?.text ?? "");
    });

    onCleanup(() => {
      unsubscribeQuote();
    });
  });

  return (
    <PromptBarProvider
      onSubmit={props.onSubmit}
      streamingState={props.streamingState}
      onStopStreaming={props.onStopStreaming}
      quote={quote}
    >
      <PromptBarContent quote={quote} setQuote={setQuote} />
    </PromptBarProvider>
  );
};
